import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, addDoc, doc, deleteDoc } from 'firebase/firestore';
import { FaSpinner, FaUser, FaEnvelope, FaCheck, FaTimes } from 'react-icons/fa';

const initialForm = {
  code: '',
  type: 'campaign',
  discountType: 'percentage',
  discountValue: '',
  validFrom: '',
  validTo: '',
  maxRedemptions: '',
  active: true,
  applicableTo: ['all'],
  redemptions: 0,
  refundAmount: '',
  refundTakeoverId: '',
  refundTransactionId: '',
};

const labelClass = 'mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500';

export default function CouponsPage() {
  const [coupons, setCoupons] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [error, setError] = useState('');
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [createNotification, setCreateNotification] = useState(true);
  const [userSearch, setUserSearch] = useState('');

  useEffect(() => {
    fetchCoupons();
    fetchUsers();
  }, []);

  async function fetchCoupons() {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'coupons'));
      setCoupons(querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      setError('Failed to load coupons');
    }
    setLoading(false);
  }

  async function fetchUsers() {
    setUsersLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'users'));
      setUsers(querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('Failed to load users:', e);
    }
    setUsersLoading(false);
  }

  const createUserNotification = async (userId, couponCode, discountValue, discountType, validTo) => {
    try {
      const notification = {
        title: `You've received a ${discountValue}${discountType === 'percentage' ? '%' : '$'} off coupon!`,
        description: `Use code ${couponCode} for ${discountValue}${discountType === 'percentage' ? '%' : '$'} off a ${form.type}. Expires ${new Date(validTo).toLocaleDateString()}.`,
        type: 'coupon',
        seen: false,
        dateIssued: new Date(),
        couponCode: couponCode,
      };
      await addDoc(collection(db, 'users', userId, 'notifications'), notification);
    } catch (err) {
      console.error(`Failed to create notification for user ${userId}:`, err);
    }
  };

  async function handleAddCoupon(e) {
    if (e) e.preventDefault();
    setError('');

    try {
      const couponCode = form.code.toUpperCase() || `COUPON-${Math.random().toString(36).substring(2, 10).toUpperCase()}`;
      const validToDate = form.validTo ? new Date(form.validTo) : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

      const couponData = {
        code: couponCode,
        type: form.type,
        discountType: form.discountType,
        discountValue: Number(form.discountValue),
        validFrom: form.validFrom ? new Date(form.validFrom) : new Date(),
        validTo: validToDate,
        maxRedemptions: form.maxRedemptions ? Number(form.maxRedemptions) : null,
        active: form.active,
        applicableTo: selectedUserIds.length > 0 ? selectedUserIds : ['all'],
        redemptions: 0,
        createdAt: new Date(),
      };

      if (form.refundAmount) {
        couponData.refundAmount = Number(form.refundAmount);
      }
      if (form.refundTakeoverId) {
        couponData.refundTakeoverId = form.refundTakeoverId;
      }
      if (form.refundTransactionId) {
        couponData.refundTransactionId = form.refundTransactionId;
      }

      await addDoc(collection(db, 'coupons'), couponData);

      if (createNotification && couponData.applicableTo[0] !== 'all') {
        for (const userId of couponData.applicableTo) {
          await createUserNotification(userId, couponCode, form.discountValue, form.discountType, validToDate);
        }
      }

      setForm(initialForm);
      setSelectedUserIds([]);
      fetchCoupons();
      alert(`Coupon ${couponCode} created successfully!`);
    } catch (e) {
      setError('Failed to add coupon');
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this coupon?')) return;
    try {
      await deleteDoc(doc(db, 'coupons', id));
      fetchCoupons();
    } catch (e) {
      setError('Failed to delete coupon');
    }
  }

  const getCouponStatus = (coupon) => {
    const now = new Date();
    const validTo = coupon.validTo?.seconds ? new Date(coupon.validTo.seconds * 1000) : new Date(coupon.validTo);
    if (!coupon.active) return { status: 'Inactive', color: '#64748b' };
    if (now > validTo) return { status: 'Expired', color: '#dc2626' };
    return { status: 'Active', color: '#059669' };
  };

  const filteredUsers = users.filter(
    (u) =>
      !userSearch ||
      (u.name || '').toLowerCase().includes(userSearch.toLowerCase()) ||
      (u.email || '').toLowerCase().includes(userSearch.toLowerCase())
  );

  const activeCount = coupons.filter((c) => getCouponStatus(c).status === 'Active').length;

  return (
    <>
      <div className="f-page-enter mx-auto max-w-[1920px] px-4 pb-10 md:px-6">
        <div className="f-header rounded-xl border border-slate-200/80 shadow-sm">
          <div>
            <h1 className="f-title">Coupons</h1>
            <p className="f-subtitle mb-0">Create discount codes and control who can redeem them</p>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
            <span className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 font-medium tabular-nums">
              {coupons.length} total
            </span>
            <span className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 font-medium text-emerald-800 tabular-nums">
              {activeCount} active
            </span>
          </div>
        </div>

        {error && (
          <div
            className="mb-6 flex items-center justify-between gap-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900"
            role="alert"
          >
            <span>{error}</span>
            <button type="button" className="f-button f-button-secondary shrink-0 !min-h-8 !h-8 !px-3 !text-xs" onClick={() => setError('')}>
              Dismiss
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,400px)_1fr] lg:items-start">
          {/* Create form */}
          <div className="f-card space-y-5">
            <div className="border-b border-slate-100 pb-4">
              <h2 className="text-base font-semibold text-slate-900">New coupon</h2>
              <p className="mt-1 text-xs text-slate-500">Required: discount value. Code auto-generates if left blank.</p>
            </div>

            <form onSubmit={handleAddCoupon} className="flex flex-col gap-4">
              <div>
                <label className={labelClass} htmlFor="coupon-code">
                  Code
                </label>
                <input
                  id="coupon-code"
                  className="f-input w-full"
                  placeholder="e.g. SUMMER20"
                  value={form.code}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelClass} htmlFor="coupon-type">
                    Applies to
                  </label>
                  <select
                    id="coupon-type"
                    className="f-input w-full"
                    value={form.type}
                    onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                  >
                    <option value="campaign">Campaign</option>
                    <option value="takeover">Takeover</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass} htmlFor="discount-type">
                    Discount type
                  </label>
                  <select
                    id="discount-type"
                    className="f-input w-full"
                    value={form.discountType}
                    onChange={(e) => setForm((f) => ({ ...f, discountType: e.target.value }))}
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed ($)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className={labelClass} htmlFor="discount-value">
                  Value <span className="text-rose-600">*</span>
                </label>
                <input
                  id="discount-value"
                  className="f-input w-full"
                  type="number"
                  min="0"
                  step="any"
                  value={form.discountValue}
                  onChange={(e) => setForm((f) => ({ ...f, discountValue: e.target.value }))}
                />
              </div>

              <div>
                <label className={labelClass} htmlFor="max-redemptions">
                  Max redemptions
                </label>
                <input
                  id="max-redemptions"
                  className="f-input w-full"
                  type="number"
                  min="1"
                  placeholder="Unlimited if empty"
                  value={form.maxRedemptions}
                  onChange={(e) => setForm((f) => ({ ...f, maxRedemptions: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelClass} htmlFor="valid-from">
                    Valid from
                  </label>
                  <input
                    id="valid-from"
                    className="f-input w-full"
                    type="datetime-local"
                    value={form.validFrom}
                    onChange={(e) => setForm((f) => ({ ...f, validFrom: e.target.value }))}
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="valid-to">
                    Valid to
                  </label>
                  <input
                    id="valid-to"
                    className="f-input w-full"
                    type="datetime-local"
                    value={form.validTo}
                    onChange={(e) => setForm((f) => ({ ...f, validTo: e.target.value }))}
                  />
                </div>
              </div>

              <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary/30"
                  checked={form.active}
                  onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                />
                Coupon is active
              </label>

              {form.type === 'takeover' && (
                <div className="space-y-3 rounded-xl border border-amber-200/80 bg-amber-50/60 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-amber-900/80">Refund (optional)</p>
                  <div>
                    <label className={labelClass} htmlFor="refund-amt">
                      Refund amount
                    </label>
                    <input
                      id="refund-amt"
                      className="f-input w-full !text-sm"
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={form.refundAmount}
                      onChange={(e) => setForm((f) => ({ ...f, refundAmount: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className={labelClass} htmlFor="refund-takeover">
                      Takeover ID
                    </label>
                    <input
                      id="refund-takeover"
                      className="f-input w-full !text-sm"
                      placeholder="Takeover ID"
                      value={form.refundTakeoverId}
                      onChange={(e) => setForm((f) => ({ ...f, refundTakeoverId: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className={labelClass} htmlFor="refund-txn">
                      Transaction ID
                    </label>
                    <input
                      id="refund-txn"
                      className="f-input w-full !text-sm"
                      placeholder="Transaction ID"
                      value={form.refundTransactionId}
                      onChange={(e) => setForm((f) => ({ ...f, refundTransactionId: e.target.value }))}
                    />
                  </div>
                </div>
              )}

              <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
                <p className={labelClass}>Audience</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <button type="button" className="f-button f-button-secondary !min-h-8 !h-8 !px-3 !text-xs" onClick={() => setShowUserModal(true)}>
                    {selectedUserIds.length === 0 ? 'All users' : `${selectedUserIds.length} selected`}
                  </button>
                  {selectedUserIds.length > 0 && (
                    <button
                      type="button"
                      className="text-xs font-semibold text-rose-600 underline-offset-2 hover:underline"
                      onClick={() => setSelectedUserIds([])}
                    >
                      Clear selection
                    </button>
                  )}
                </div>
                {selectedUserIds.length > 0 && (
                  <label className="mt-3 flex cursor-pointer items-center gap-2 text-xs text-slate-600">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-primary"
                      checked={createNotification}
                      onChange={(e) => setCreateNotification(e.target.checked)}
                    />
                    Send in-app notification to selected users
                  </label>
                )}
              </div>

              <button type="submit" className="f-button w-full sm:w-auto" disabled={!form.discountValue}>
                Create coupon
              </button>
            </form>
          </div>

          {/* Table */}
          <div className="f-card overflow-hidden p-0">
            <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50/90 px-5 py-4">
              <h2 className="text-base font-semibold text-slate-900">All coupons</h2>
              {loading && <FaSpinner className="fa-spin text-primary" aria-hidden />}
            </div>
            <div className="overflow-x-auto">
              <table className="f-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Type</th>
                    <th>Discount</th>
                    <th>Uses</th>
                    <th>Valid to</th>
                    <th>Target</th>
                    <th>Status</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && coupons.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-16 text-center text-slate-500">
                        <FaSpinner className="fa-spin mb-2 inline text-xl text-primary" aria-hidden />
                        <div>Loading coupons…</div>
                      </td>
                    </tr>
                  ) : coupons.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-14 text-center text-sm text-slate-500">
                        No coupons yet. Create one using the form.
                      </td>
                    </tr>
                  ) : (
                    coupons.map((c) => {
                      const status = getCouponStatus(c);
                      const validToDate = c.validTo?.seconds ? new Date(c.validTo.seconds * 1000) : new Date(c.validTo);
                      const redemptionText = c.maxRedemptions
                        ? `${c.redemptions || 0}/${c.maxRedemptions}`
                        : `${c.redemptions || 0}/∞`;

                      return (
                        <tr key={c.id}>
                          <td>
                            <code className="font-semibold text-primary">{c.code}</code>
                            {c.refundAmount != null && (
                              <div className="mt-0.5 text-[10px] text-slate-500">Refund ${Number(c.refundAmount).toFixed(2)}</div>
                            )}
                          </td>
                          <td className="capitalize">{c.type}</td>
                          <td>{c.discountType === 'percentage' ? `${c.discountValue}%` : `$${c.discountValue}`}</td>
                          <td>
                            <span className="font-mono text-xs text-slate-700">{redemptionText}</span>
                          </td>
                          <td className="text-xs text-slate-500">
                            {validToDate.toLocaleDateString()}{' '}
                            {validToDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td>
                            {c.applicableTo && c.applicableTo[0] === 'all' ? (
                              <span className="text-slate-500">All users</span>
                            ) : (
                              <span className="font-semibold text-primary">{c.applicableTo?.length} users</span>
                            )}
                          </td>
                          <td>
                            <span
                              className="inline-flex rounded-md px-2 py-0.5 text-[11px] font-semibold"
                              style={{
                                color: status.color,
                                backgroundColor: `${status.color}1a`,
                              }}
                            >
                              {status.status}
                            </span>
                          </td>
                          <td className="text-right">
                            <button
                              type="button"
                              className="f-button f-button-secondary !min-h-8 !h-8 !px-2 !text-xs text-rose-600 hover:border-rose-300 hover:bg-rose-50"
                              onClick={() => handleDelete(c.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {showUserModal && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm"
          role="presentation"
          onClick={() => setShowUserModal(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="coupon-user-modal-title"
            className="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50/90 px-5 py-4">
              <h2 id="coupon-user-modal-title" className="text-lg font-semibold text-slate-900">
                Select users
              </h2>
              <button
                type="button"
                className="flex h-10 w-10 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-200/80 hover:text-slate-800"
                onClick={() => setShowUserModal(false)}
                aria-label="Close"
              >
                <FaTimes className="text-lg" />
              </button>
            </div>

            <div className="border-b border-slate-100 p-4">
              <input
                type="search"
                className="f-input w-full"
                placeholder="Search by name or email…"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
              />
            </div>

            <div className="min-h-[200px] flex-1 overflow-y-auto p-2">
              {usersLoading ? (
                <div className="flex flex-col items-center justify-center gap-2 py-12 text-slate-500">
                  <FaSpinner className="fa-spin text-2xl text-primary" />
                  <span className="text-sm">Loading users…</span>
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {filteredUsers.map((user) => {
                    const selected = selectedUserIds.includes(user.id);
                    return (
                      <li key={user.id}>
                        <button
                          type="button"
                          className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition ${
                            selected ? 'bg-primary/10 ring-1 ring-primary/25' : 'hover:bg-slate-50'
                          }`}
                          onClick={() => {
                            if (selected) {
                              setSelectedUserIds(selectedUserIds.filter((id) => id !== user.id));
                            } else {
                              setSelectedUserIds([...selectedUserIds, user.id]);
                            }
                          }}
                        >
                          <span
                            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                              selected ? 'border-primary bg-primary text-white' : 'border-slate-300 bg-white'
                            }`}
                          >
                            {selected && <FaCheck className="text-[10px]" aria-hidden />}
                          </span>
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-200 text-slate-600">
                            <FaUser className="text-sm" aria-hidden />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-medium text-slate-900">{user.name || '—'}</span>
                            <span className="flex items-center gap-1 truncate text-xs text-slate-500">
                              <FaEnvelope className="shrink-0 opacity-70" aria-hidden />
                              {user.email || '—'}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50/90 px-4 py-3">
              <button type="button" className="f-button" onClick={() => setShowUserModal(false)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
